import React, { useEffect, useState } from 'react';
import { Checkbox, Flex, Input, Modal, Radio, Select, Typography } from 'antd';
import type { CloudDeck } from '@lingoping/core';

const { Text } = Typography;

export type ImportTarget = { kind: 'new'; name: string } | { kind: 'existing'; deckId: string };

interface Props {
  open: boolean;
  /** What the words came from: a file name, or a short label for pasted text. */
  sourceName: string;
  suggestedName: string;
  totalWords: number;
  /** Words the learner already knows or is learning: they stay in the deck. */
  alreadyKnown: number;
  /** The learner's own decks (public decks cannot be added to). */
  decks: CloudDeck[];
  saving?: boolean;
  onCancel: () => void;
  onSave: (target: ImportTarget, follow: boolean) => void;
}

/** "Where should these words go?": a new deck with a name, or one of the learner's own decks. */
export const ImportDeckModal: React.FC<Props> = ({
  open,
  sourceName,
  suggestedName,
  totalWords,
  alreadyKnown,
  decks,
  saving,
  onCancel,
  onSave,
}) => {
  const [choice, setChoice] = useState<'new' | 'existing'>('new');
  const [name, setName] = useState(suggestedName);
  const [deckId, setDeckId] = useState<string | undefined>(decks[0]?.id);
  const [follow, setFollow] = useState(true);

  // Starts fresh every time the prompt opens.
  useEffect(() => {
    if (!open) return;
    setChoice('new');
    setName(suggestedName);
    setDeckId(decks[0]?.id);
    setFollow(true);
  }, [open, suggestedName, decks]);

  const canSave = choice === 'new' ? name.trim().length > 0 : !!deckId;
  const save = () => {
    if (!canSave || saving) return;
    onSave(
      choice === 'new' ? { kind: 'new', name: name.trim() } : { kind: 'existing', deckId: deckId! },
      follow
    );
  };

  return (
    <Modal
      title="Where should these words go?"
      open={open}
      onCancel={onCancel}
      onOk={save}
      okText="Save deck"
      okButtonProps={{ disabled: !canSave }}
      confirmLoading={saving}
      cancelText="Cancel"
      destroyOnHidden
    >
      <Flex vertical gap={16}>
        <Text type="secondary">
          {totalWords} word{totalWords === 1 ? '' : 's'} found in <strong>{sourceName}</strong>.{' '}
          {alreadyKnown > 0
            ? `${alreadyKnown} you already know or are learning ${alreadyKnown === 1 ? 'is' : 'are'} in the deck too, with their status.`
            : 'All of them are new to you.'}
        </Text>
        <Radio.Group
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          aria-label="Deck for the imported words"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
            <Radio value="new">
              <strong>Create a new deck</strong>
            </Radio>
            {choice === 'new' && (
              <Flex vertical gap={4} style={{ paddingLeft: 24, marginTop: 8 }}>
                <label htmlFor="new-deck-name" style={{ fontSize: 13, color: '#475569' }}>
                  Deck name
                </label>
                <Input
                  id="new-deck-name"
                  value={name}
                  maxLength={120}
                  onChange={(e) => setName(e.target.value)}
                  onPressEnter={save}
                />
              </Flex>
            )}
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
            <Radio value="existing" disabled={decks.length === 0}>
              <strong>Add to one of your decks</strong>
            </Radio>
            {decks.length === 0 && (
              <div style={{ paddingLeft: 24, marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  You don&apos;t have a deck of your own yet.
                </Text>
              </div>
            )}
            {choice === 'existing' && (
              <div style={{ paddingLeft: 24, marginTop: 8 }}>
                <Select
                  aria-label="Choose a deck"
                  style={{ width: '100%' }}
                  value={deckId}
                  onChange={setDeckId}
                  options={decks.map((d) => ({
                    value: d.id,
                    label: `${d.title} · ${d.word_count} word${d.word_count === 1 ? '' : 's'}`,
                  }))}
                />
              </div>
            )}
          </div>
        </Radio.Group>
        <Checkbox checked={follow} onChange={(e) => setFollow(e.target.checked)}>
          Follow this deck
        </Checkbox>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Nothing is added to your learning yet. You choose word by word.
        </Text>
      </Flex>
    </Modal>
  );
};
